<template>
  <div class="checkout">
    <v-layout v-bind="layout">
      <v-flex md12 lg8>
        <v-form ref="checkout-form" v-model="valid" lazy-validation>
          <v-stepper v-model="step" alt-labels non-linear class="elevation-2 px-3 py-3">
            <v-stepper-header>
              <v-stepper-step editable edit-icon="check" step="1" :complete="validAddress">
                Shipping / Billing Info
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step editable edit-icon="check" step="2" :complete="validShipping">
                Shipping Method
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :editable="validShipping" edit-icon="check" step="3" :complete="validReview" disabled>
                Review Order
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :editable="validReview" edit-icon="check" step="4" :complete="validCreditCard" disabled>
                Payment
              </v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <v-stepper-content step="1">
                <v-layout v-bind="layout" v-if="cart">
                  <v-flex md6>
                    <contact-input
                      contactTitle="Shipping Information"
                      :showResCheckBox="true"
                      :contact="cart.shipping_address"
                      @update-contact="cart.shipping_address = $event"
                    />
                  </v-flex>
                  <v-flex md6>
                    <contact-input
                      contactTitle="Billing Information"
                      :showUseShipping="true"
                      :contact="cart.billing_address"
                      @copy-address="copyAddress = $event"
                      @update-contact="cart.billing_address = $event"
                    />
                  </v-flex>
                </v-layout>
                <v-layout>
                  <v-flex md9>
                    <v-tooltip top class="ml-5" v-if="hasProxy">
                      <v-switch
                        v-model="createCustomer"
                        @change="cart.create_customer = $event"
                        slot="activator"
                        class="right"
                        label="Create Customer Account"
                      />
                      <span>Create a new customer account and include cart items</span>
                    </v-tooltip>
                  </v-flex>
                  <v-flex md3 mt-2>
                    <v-btn class="right" :color="validAddress ? 'primary' : 'disabled'" :depressed="!validAddress" @click.native="saveCartAddresses()">
                      Shipping Method
                      <v-icon right>mdi-arrow-right</v-icon>
                    </v-btn>
                  </v-flex>
                </v-layout>
              </v-stepper-content>

              <v-stepper-content step="2">
                <v-layout row>
                  <v-flex md12>
                    <shipping-method :shipping-methods="shippingMethods"></shipping-method>
                  </v-flex>
                </v-layout>
                <div class="right">
                  <v-btn flat @click.native="step = 1">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Shipping / Billing Info
                  </v-btn>
                  <v-btn :color="validShipping ? 'primary' : 'disabled'" :depressed="!validShipping" @click="saveShippingMethod()">
                    Review Order
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </v-stepper-content>

              <v-stepper-content step="3">
                <v-layout row>
                  <v-flex md12>
                    <review-order
                      v-if="cart"
                      @update-step="step = $event"
                    ></review-order>
                  </v-flex>
                </v-layout>
                <div class="right">
                  <v-btn flat @click="loadShippingMethods()" @click.native="step = 2">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Shipping Method
                  </v-btn>
                  <v-btn :color="agree ? 'primary' : 'disabled'" :depressed="!agree" @click="saveAgreement()">
                    Payment
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </v-stepper-content>

              <v-stepper-content step="4">
                <v-layout row>
                  <v-flex md12>
                    <payment v-model="creditCard"
                             :credit-card="creditCard"
                             :total-price="cart.total_price"
                             :payment-methods="paymentMethods"
                             @change="updatePaymentMethodId"/>
                  </v-flex>
                </v-layout>
                <div class="right">
                  <v-btn flat @click.native="step = 3">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Review Order
                  </v-btn>
                  <v-btn :disabled="!validCreditCard" color="primary" @click="makePayment()">
                    Pay
                  </v-btn>
                </div>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-form>
      </v-flex>
      <v-flex sm4>
        <cart-summary type="checkout"/>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
  import api from '@/api/gungho'
  import Events from '@/api/events'
  import Cart from '../cart'
  import ContactInput from '../widgets/contact-input'
  import ShippingMethod from './shipping-method'
  import ReviewOrder from './review-order'
  import Payment from './payment'

  export default {
    metaInfo: {
      title: 'Checkout'
    },

    components: {
      cartSummary: Cart,
      contactInput: ContactInput,
      shippingMethod: ShippingMethod,
      reviewOrder: ReviewOrder,
      payment: Payment
    },

    data () {
      return {
        valid: true,
        step: 0,
        createCustomer: true,
        paymentMethods: [],
        shippingMethods: false,
        copyAddress: false,
        creditCard: {
          payment_method_id: '',
          name: '',
          number: '',
          expiration: '',
          security: '',
          store: false
        }
      }
    },

    watch: {
      'cart.shipping_address': {
        handler (val) {
          if (this.copyAddress === true) this.makeAddressCopy()
        },
        deep: true
      }
    },

    computed: {
      hasProxy () {
        if (!this.$store.state.user.features) return false
        return this.$store.getters.features.includes('proxy')
      },

      layout () {
        const layout = {}
        if (this.$vuetify.breakpoint.smAndDown) layout.column = true
        return layout
      },

      user: {
        get () {
          return this.$store.state.user
        },
        set (value) {
          this.$store.commit('saveUser', value)
        }
      },

      cart: {
        get () {
          return this.$store.state.cart
        },
        set (value) {
          this.$store.commit('saveCart', value)
        }
      },

      agree () {
        return this.$store.state.agree
      },

      validCart () {
        return this.cart && this.cart.cart_items && this.cart.cart_items.length > 0
      },

      validAddress () {
        return this.validCart && this.shippingAddressComplete() && this.billingAddressComplete()
      },

      validShipping () {
        return this.validCart && this.validAddress && this.cart.shipping_method !== null
      },

      validReview () {
        return this.validShipping && this.agree === true
      },

      validCreditCard () {
        if (this.creditCard.payment_method_id) return true
        return this.creditCard.name.length > 3 &&
               this.creditCard.number.length >= 15 &&
               this.creditCard.expiration.length > 4 &&
               this.creditCard.security.length >= 3
      }
    },

    methods: {
      makeAddressCopy () {
        Object.keys(this.cart.shipping_address).forEach(key => {
          this.cart.billing_address[key] = this.cart.shipping_address[key]
        })
      },

      present (test) {
        return test != null && test.length > 0
      },

      shippingAddressComplete () {
        return this.present(this.cart.shipping_address.street1) &&
               this.present(this.cart.shipping_address.city) &&
               this.present(this.cart.shipping_address.country) &&
               this.present(this.cart.shipping_address.email) &&
               this.present(this.cart.shipping_address.phone)
      },

      billingAddressComplete () {
        return this.present(this.cart.billing_address.street1) &&
               this.present(this.cart.billing_address.city) &&
               this.present(this.cart.billing_address.country) &&
               this.present(this.cart.billing_address.email) &&
               this.present(this.cart.billing_address.phone)
      },

      loadShippingMethods () {
        if (!this.validCart || !this.validAddress) return false
        this.shippingMethods = false
        api.cartShippingMethods(this.$store.state.cart.id).then(data => {
          console.log('Load Shipping Methods')
          this.shippingMethods = data.shipping_methods
        }).catch(error => {
          console.log(error)
        })
      },

      loadPaymentMethods () {
        api.paymentMethods().then(data => {
          this.paymentMethods = data.payment_methods
        }).catch(error => {
          console.log(error)
        })
      },

      sleep (time) {
        return new Promise((resolve) => setTimeout(resolve, time))
      },

      saveCart () {
        this.$store.dispatch('saveCart', this.cart).then((resolve, reject) => {
          this.sleep(500).then(() => { this.loadShippingMethods() })
        })
      },

      updateContact (to, from) {
        to = from
      },

      saveCartAddresses () {
        console.log('Saving Cart addresses')
        if (!this.validAddress) {
          Events.$emit('error',
                       'Please enter your shipping and/or billing information')
          return false
        }

        this.step = 2
        Events.$emit('toast', 'Saved Shipping / Billing Info')
        this.saveCart()
      },

      saveShippingMethod () {
        if (!this.validShipping) {
          Events.$emit('error', 'Please select a shipping method')
          return false
        }
        this.step = 3
        Events.$emit('toast', 'Saved Shipping Method')
        this.saveCart()
      },

      saveAgreement () {
        if (!this.agree) {
          Events.$emit('error',
                       'Please check the "I have reviewed my order"... box')
          return false
        }
        this.step = 4

        return true
      },

      updatePaymentMethodId (id) {
        this.creditCard.payment_method_id = id
      },

      makePayment () {
        var data = {
          'payment': {
            'user_id': this.user.id,
            'order_id': this.cart.order_id,
            'shipping_method': this.cart.shipping_method,
            'kind': 'CreditCard',
            'total': {
              'amount': this.cart.total_price,
              'tax': this.cart.total_tax,
              'shipping': this.cart.total_shipping
            }
          }
        }

        if (this.creditCard.payment_method_id) {
          data.payment.payment_method_id = this.creditCard.payment_method_id
        } else {
          data.payment.credit_card = {
            'name': this.creditCard.name,
            'number': this.creditCard.number,
            'expiration': this.creditCard.expiration,
            'cvv': this.creditCard.security,
            'save': this.creditCard.save
          }
        }

        api.payment(this.$store.state.cart.id, data).then(data => {
          if (data.charged === true) {
            Events.$emit('toast', data.response_text)
            api.createOrder(this.$store.state.cart.id).then(data => {
              this.$router.push('/orders/' + data)
            }).catch(error => {
              console.log('Error', error)
            })
          } else {
            Events.$emit('error', data.response_text)
            console.log('Payment Error Response: ', data.response_text)
          }
        }).catch(error => {
          console.log(error)
        })
      }
    },

    beforeMount () {
      this.$store.dispatch('loadCart')
      this.$store.dispatch('loadStates')
      this.$store.dispatch('loadCountries')
      this.loadPaymentMethods()
      this.loadShippingMethods()
      if (this.$route.query.step) this.step = this.$route.query.step
    }
  }
</script>



// WEBPACK FOOTER //
// index.vue?eff97ba6